// src/lib/logger.ts
import { v4 as uuid } from "uuid";

type Level = "trace" | "debug" | "info" | "warn" | "error";
type Sink = (rec: LogRecord) => void | Promise<void>;

export type LogRecord = {
  id: string;                // unique id per record
  ts: string;                // ISO timestamp
  lvl: Level;
  msg: string;
  name?: string;             // component/feature name (scope)
  ctx?: Record<string, unknown>; // extra props (scrubbed)
  user?: { id?: string; email?: string; role?: string } | null;
  route?: string;
  sessionId?: string;
  appVersion?: string;
  env?: string;
};

let currentUser: LogRecord["user"] = null;
let currentRoute: string | undefined;
const sessionId = uuid();
const appVersion = import.meta.env.VITE_APP_VERSION;
const env = import.meta.env.MODE;

const sinks: Array<Sink> = [];

function scrub(obj?: Record<string, unknown>) {
  if (!obj) return undefined;
  // remove obvious PII keys; extend as needed
  const banned = ["password", "ssn", "token", "idtoken", "refreshtoken"];
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(obj)) {
    out[k] = banned.includes(k.toLowerCase()) ? "[REDACTED]" : v;
  }
  return out;
}

async function emit(rec: LogRecord) {
  for (const sink of sinks) {
    try {
      await sink(rec);
    } catch {
      // never throw from the logger
    }
  }
}

function base(rec: Partial<LogRecord>): LogRecord {
  return {
    id: uuid(),
    ts: new Date().toISOString(),
    lvl: "info",
    msg: "",
    user: currentUser,
    route: currentRoute,
    sessionId,
    appVersion,
    env,
    ...rec,
    ctx: scrub(rec.ctx),
  };
}

export const Logger = {
  /** Add one or more sinks (does not replace existing) */
  use: (...next: Array<Sink>) => {
    sinks.push(...next);
  },
  /** Remove all sinks */
  clearSinks: () => {
    sinks.splice(0, sinks.length);
  },
  setUser: (user: LogRecord["user"]) => {
    currentUser = user;
  },
  clearUser: () => {
    currentUser = null;
  },
  setRoute: (route: string | undefined) => {
    currentRoute = route;
  },
  scope(name: string) {
    return {
      trace: (msg: string, ctx?: Record<string, unknown>) =>
        emit(base({ lvl: "trace", msg, name, ctx })),
      debug: (msg: string, ctx?: Record<string, unknown>) =>
        emit(base({ lvl: "debug", msg, name, ctx })),
      info: (msg: string, ctx?: Record<string, unknown>) =>
        emit(base({ lvl: "info", msg, name, ctx })),
      warn: (msg: string, ctx?: Record<string, unknown>) =>
        emit(base({ lvl: "warn", msg, name, ctx })),
      error: (msg: string, ctx?: Record<string, unknown>) =>
        emit(base({ lvl: "error", msg, name, ctx })),
    };
  },
  trace: (msg: string, ctx?: Record<string, unknown>) =>
    emit(base({ lvl: "trace", msg, ctx })),
  debug: (msg: string, ctx?: Record<string, unknown>) =>
    emit(base({ lvl: "debug", msg, ctx })),
  info: (msg: string, ctx?: Record<string, unknown>) =>
    emit(base({ lvl: "info", msg, ctx })),
  warn: (msg: string, ctx?: Record<string, unknown>) =>
    emit(base({ lvl: "warn", msg, ctx })),
  error: (msg: string, ctx?: Record<string, unknown>) =>
    emit(base({ lvl: "error", msg, ctx })),
};

/* ----------------------- Console sink (fixed) ----------------------- */

const colors: Record<Level, string> = {
  trace: "color:#999",    // gray
  debug: "color:#0af",    // cyan
  info: "color:#2d2",    // green
  warn: "color:#e6b800", // yellow
  error: "color:#e00",    // red
};

const levelOrder: Record<Level, number> = {
  trace: 0, debug: 1, info: 2, warn: 3, error: 4,
};

/** Returns a Sink that prints nicely to the console (with scope). */
export const consoleSink = (minLevel: Level = "debug"): Sink => {
  return (rec: LogRecord) => {
    if (levelOrder[rec.lvl] < levelOrder[minLevel]) return;

    const levelStyle = colors[rec.lvl];
    const scopeTag = rec.name ? `%c[${rec.name}]` : "";
    const scopeStyle = rec.name ? "color:#555" : "";

    const fn =
      rec.lvl === "trace" ? console.debug // `console.trace` prints stack; use debug for trace
        : (console[rec.lvl] as (...args: Array<any>) => void) 

    // Example:
    // [DEBUG][Dashboard] Message {ctx} {meta...}
    fn(
      `%c[${rec.lvl.toUpperCase()}]${scopeTag}`,
      levelStyle,
      ...(rec.name ? [scopeStyle] : []),
      rec.msg,
      rec.ctx ?? "",
      // extra useful meta (shown collapsed)
      {
        route: rec.route,
        user: rec.user ?? undefined,
        sessionId: rec.sessionId,
        appVersion: rec.appVersion,
        ts: rec.ts,
        env: rec.env,
      }
    );
  };
};

declare global {
  interface Window { Logger?: typeof Logger }
}
if (typeof window !== "undefined" && import.meta.env.DEV) {
  window.Logger = Logger;
}

